import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, ListPlus, ClipboardPaste } from "lucide-react";
import PasteDialog from "./PasteDialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useEntity } from "@/context/EntityContext";
import EntryLineRow from "./EntryLineRow";
import EntryTotals from "./EntryTotals";
import { newLine, duplicateLine, isBlank, lineErrors, toPayload } from "./multiEntry";

const HEADERS_BIZ = ["#", "Date", "Account type", "Department", "GL code", "Account", "Memo", "Amount", ""];
const HEADERS_PER = ["#", "Date", "Type", "Category", "Account", "Memo", "Amount", ""];
const COLS_BIZ = "28px 130px 140px 150px 170px 160px minmax(140px,1fr) 140px 64px";
const COLS_PER = "28px 130px 120px 170px 170px minmax(160px,1fr) 140px 64px";

export default function MultiTransactionForm({ currency = "USD", onDone, onCancel }) {
  const { currentEntity } = useEntity();
  const isBusiness = currentEntity?.workspace_type === "business";
  const [lines, setLines] = useState(() => [newLine(), newLine()]);
  const [refs, setRefs] = useState({ categories: { expense: [], income: [], saving: [] }, accounts: [], coa: [] });
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [paste, setPaste] = useState({ open: false, text: "" });

  useEffect(() => {
    const reqs = [api.get("/categories"), api.get("/accounts")];
    if (isBusiness) reqs.push(api.get("/coa"));
    Promise.all(reqs).then((r) => setRefs({
      categories: r[0].data || { expense: [], income: [], saving: [] },
      accounts: r[1].data || [],
      coa: isBusiness ? r[2].data || [] : [],
    })).catch((e) => setErrorMsg(e?.message || "Failed to load lists"));
  }, [isBusiness]);

  const update = (key, patch) => setLines((ls) => ls.map((l) => {
    if (l.key !== key) return l;
    const unmatched = { ...(l.unmatched || {}) };
    Object.keys(patch).forEach((k) => delete unmatched[k]);
    if ("business_kind" in patch) delete unmatched.type;
    return { ...l, ...patch, unmatched };
  }));
  const onGridPaste = (e) => {
    if (paste.open) return;
    const t = e.clipboardData?.getData("text") || "";
    if (!/[\t\n]/.test(t.trim())) return;
    e.preventDefault();
    setPaste({ open: true, text: t });
  };
  const addPasted = (pasted) => {
    setLines((ls) => [...ls.filter((l) => !isBlank(l)), ...pasted]);
    setPaste({ open: false, text: "" });
    toast.success(`${pasted.length} line${pasted.length === 1 ? "" : "s"} pasted`);
  };
  const addLines = (n = 1) => setLines((ls) => {
    const out = [...ls];
    for (let i = 0; i < n; i++) out.push(newLine(out[out.length - 1]));
    return out;
  });
  const dup = (key) => setLines((ls) => {
    const i = ls.findIndex((l) => l.key === key);
    return [...ls.slice(0, i + 1), duplicateLine(ls[i]), ...ls.slice(i + 1)];
  });
  const remove = (key) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));
  const onEnter = (i) => { if (i === lines.length - 1) addLines(1); };

  const filled = lines.filter((l) => !isBlank(l));
  const invalid = filled.some((l) => Object.values(lineErrors(l)).some(Boolean));

  const save = async () => {
    setErrorMsg("");
    setShowErrors(true);
    if (!filled.length) return setErrorMsg("Enter at least one line");
    if (invalid) return setErrorMsg("Fix the highlighted cells before saving");
    setSaving(true);
    try {
      const transactions = filled.map((l) => toPayload(l, refs.accounts, currency));
      const { data } = await api.post("/transactions/bulk", { transactions });
      onDone?.(data.length);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setErrorMsg(typeof msg === "string" ? msg : "Save failed — nothing was posted");
    } finally {
      setSaving(false);
    }
  };

  const ctx = useMemo(() => ({ isBusiness, currency, ...refs }), [isBusiness, currency, refs]);
  const headers = isBusiness ? HEADERS_BIZ : HEADERS_PER;
  const cols = isBusiness ? COLS_BIZ : COLS_PER;

  return (
    <div data-testid="multi-transaction-form" className="space-y-4 min-w-0" onPaste={onGridPaste}>
      <div className="rounded-xl border border-[var(--divider)] overflow-x-auto">
        <div className="min-w-[1080px]">
          <div className="grid gap-2 px-3 py-2 bg-[var(--card)] border-b border-[var(--divider)] text-[10px] tracking-[0.15em] uppercase text-[var(--muted-text)]"
               style={{ gridTemplateColumns: cols }}>
            {headers.map((h) => <span key={h} className={h === "Amount" ? "text-right" : ""}>{h}</span>)}
          </div>
          <div className="max-h-[50vh] overflow-y-auto" data-testid="ml-rows">
            {lines.map((l, i) => (
              <EntryLineRow key={l.key} line={l} index={i} ctx={ctx} gridCols={cols}
                            errors={showErrors && !isBlank(l) ? lineErrors(l) : Object.fromEntries(Object.keys(l.unmatched || {}).map((k) => [k, true]))}
                            onChange={update} onDuplicate={dup} onRemove={remove}
                            onEnterLast={onEnter} canRemove={lines.length > 1} />
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" data-testid="ml-add-line" onClick={() => addLines(1)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add line
          </Button>
          <Button type="button" variant="ghost" size="sm" data-testid="ml-add-5" onClick={() => addLines(5)}>
            <ListPlus className="w-3.5 h-3.5 mr-1" /> Add 5 lines
          </Button>
          <Button type="button" variant="ghost" size="sm" data-testid="ml-paste-btn"
                  onClick={() => setPaste({ open: true, text: "" })}>
            <ClipboardPaste className="w-3.5 h-3.5 mr-1" /> Paste from spreadsheet
          </Button>
        </div>
        <EntryTotals lines={filled} accounts={refs.accounts} currency={currency} />
      </div>

      <p className="text-[11px] text-[var(--muted-text)]">
        Blank lines are skipped. Press Enter in the last amount to add a line. Paste (Ctrl/Cmd+V) rows from Excel or Sheets anywhere in the grid. Lines save together: if one fails, none are posted.
      </p>
      <PasteDialog open={paste.open} initialText={paste.text} ctx={ctx} onConfirm={addPasted}
                   onOpenChange={(o) => setPaste((p) => ({ ...p, open: o }))} />
      {errorMsg && <p data-testid="ml-error" className="text-xs text-[#E63946]">{errorMsg}</p>}

      <div className="flex justify-end items-center gap-2">
        <span data-testid="ml-count" className="text-xs text-[var(--muted-text)] mr-auto">
          {filled.length} line{filled.length === 1 ? "" : "s"} ready
        </span>
        {onCancel && <Button type="button" variant="ghost" data-testid="ml-cancel" onClick={onCancel}>Cancel</Button>}
        <Button type="button" data-testid="ml-submit" disabled={saving} onClick={save}
                className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">
          {saving ? "Saving…" : `Save ${filled.length || ""} transaction${filled.length === 1 ? "" : "s"}`}
        </Button>
      </div>
    </div>
  );
}
